import { useEffect, useContext, useState, useCallback } from 'react'
import {
  useIsConnectionRestored,
  useTonConnectUI,
  useTonWallet,
  Wallet,
} from '@tonconnect/ui-react'
import { BackendTokenContext } from './BackendTokenContext'
import { backendAuth } from './backend-auth'
import {
  getAuth,
  onAuthStateChanged,
  signInWithCustomToken,
  signOut,
} from 'firebase/auth'
import { firebaseApp } from '@/helpers/firebase'
import { useAppDispatch } from '@/state/hooks'
import { setUser } from '@/state/userSlice'

const localStorageKey = 'my-dapp-auth-token'
const payloadTTLMS = 1000 * 60 * 20

interface AppAuthProviderProps {
  children: React.ReactNode
}

export const AppAuthProvider = ({ children }: AppAuthProviderProps) => {
  const { setToken } = useContext(BackendTokenContext)
  const dispatch = useAppDispatch()
  const isConnectionRestored = useIsConnectionRestored()
  const wallet = useTonWallet()
  const [tonConnectUI] = useTonConnectUI()
  const [isFirebaseReady, setIsFirebaseReady] = useState(false)

  const handleDisconnect = useCallback(async () => {
    try {
      const auth = getAuth(firebaseApp)
      await signOut(auth)

      localStorage.removeItem(localStorageKey)
      setToken(null)

      dispatch(
        setUser({
          id: '',
          username: '',
          firebaseUser: null,
        })
      )

      setIsFirebaseReady(false)
      await tonConnectUI.disconnect()
      window.location.reload()
    } catch (error) {
      console.error('Error during logout:', error)
      throw error
    }
  }, [tonConnectUI, setToken, dispatch])

  useEffect(() => {
    const auth = getAuth(firebaseApp)
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      if (user) {
        console.log('Firebase user is authenticated:', user.uid)
        setIsFirebaseReady(true)
        dispatch(
          setUser({
            id: user.uid,
            username: user.uid,
            firebaseUser: user,
          })
        )
      } else {
        setIsFirebaseReady(false)
      }
    })

    return () => unsubscribe()
  }, [dispatch])

  useEffect(() => {
    const unsubscribe = tonConnectUI.onStatusChange(
      async (currentWallet: Wallet | null) => {
        if (!currentWallet) {
          try {
            await handleDisconnect()
          } catch (error) {
            console.error('Failed to handle wallet disconnect:', error)
          }
        }
      }
    )

    return () => {
      unsubscribe()
    }
  }, [tonConnectUI, handleDisconnect])

  useEffect(() => {
    if (!isConnectionRestored || !setToken) {
      return
    }

    let payloadInterval: ReturnType<typeof setInterval> | undefined

    const setupPayloadRefresh = async () => {
      if (!wallet) {
        localStorage.removeItem(localStorageKey)
        setToken(null)

        const refreshPayload = async () => {
          tonConnectUI.setConnectRequestParameters({ state: 'loading' })
          const value = await backendAuth.generatePayload()
          console.log('payload: ', value)
          if (!value) {
            tonConnectUI.setConnectRequestParameters(null)
          } else {
            tonConnectUI.setConnectRequestParameters({ state: 'ready', value })
          }
        }

        await refreshPayload()
        payloadInterval = setInterval(refreshPayload, payloadTTLMS)
        return
      }

      if (payloadInterval) {
        clearInterval(payloadInterval)
      }

      // Check if there's a stored token
      const storedToken = localStorage.getItem(localStorageKey)
      if (storedToken) {
        try {
          const auth = getAuth(firebaseApp)
          await signInWithCustomToken(auth, storedToken)
          setToken(storedToken)
          return
        } catch (error) {
          // Just log the error and clear the token, silently proceed to wallet auth
          console.error('Stored token is invalid:', error)
          localStorage.removeItem(localStorageKey)
          await handleDisconnect()
          return
        }
      }

      if (
        wallet.connectItems?.tonProof &&
        !('error' in wallet.connectItems.tonProof)
      ) {
        const result = await backendAuth.checkProof(
          wallet.connectItems.tonProof.proof,
          wallet.account
        )

        if (result) {
          try {
            const auth = getAuth(firebaseApp)
            await signInWithCustomToken(auth, result)
            setToken(result)
            localStorage.setItem(localStorageKey, result)
          } catch (error) {
            console.error('Signin Error:', error)
            alert('Signin Error')
            await handleDisconnect()
          }
        } else {
          await handleDisconnect()
        }
      } else {
        await handleDisconnect()
      }
    }

    setupPayloadRefresh()

    return () => {
      if (payloadInterval) {
        clearInterval(payloadInterval)
      }
    }
  }, [
    wallet,
    isConnectionRestored,
    setToken,
    dispatch,
    tonConnectUI,
    handleDisconnect,
  ])

  if (!isFirebaseReady && wallet) {
    return (
      <div className="auth-loading-container">
        <div className="loading-state">
          <div className="loading-spinner"></div>
          <p>Authenticating...</p>
        </div>
      </div>
    )
  }

  return <>{children}</>
}
